import { useState } from "react";
import { MessageSquare, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { posthog } from "@/lib/analytics";
import { useAuth } from "@/hooks/use-auth";

const SURVEY_ID = (import.meta.env.VITE_POSTHOG_FEEDBACK_SURVEY_ID as string | undefined) || "";

export function FeedbackButton() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!user) return null;

  const handleOpenChange = (next: boolean) => {
    if (next && !SURVEY_ID) {
      toast({
        title: "Feedback survey not configured",
        description:
          "Set VITE_POSTHOG_FEEDBACK_SURVEY_ID to your PostHog survey ID. Use the bug icon to send a detailed report instead.",
        variant: "destructive",
      });
      return;
    }
    setOpen(next);
    if (next) {
      try {
        posthog.capture("survey shown", { $survey_id: SURVEY_ID });
        posthog.capture("feedback_button_clicked", { survey_id: SURVEY_ID });
      } catch {}
    }
  };

  const handleSubmit = () => {
    const text = message.trim();
    if (text.length < 3 || submitting || !SURVEY_ID) return;
    setSubmitting(true);
    try {
      posthog.capture("survey sent", {
        $survey_id: SURVEY_ID,
        $survey_response: text,
        $survey_questions: [{ id: SURVEY_ID, question: "What's not working?" }],
      });
      toast({ title: "Thanks for the feedback", description: "We've received your note and the team will take a look." });
      setMessage("");
      setOpen(false);
    } catch (err: any) {
      toast({
        title: "Couldn't send feedback",
        description: err?.message || "Please try again, or use the bug icon for a detailed report.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = message.trim().length >= 3 && !submitting;

  return (
    <>
      <Button
        size="sm"
        onClick={() => handleOpenChange(true)}
        className="h-8 px-3 gap-1.5 bg-orange-500 hover:bg-orange-600 text-white"
        data-testid="button-open-feedback"
        aria-label="Send feedback"
        title="Send feedback"
      >
        <MessageSquare className="h-3.5 w-3.5" />
        <span className="text-xs font-semibold">Feedback</span>
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-md rounded-2xl p-5 sm:p-6"
          data-testid="dialog-feedback"
        >
          <DialogHeader className="space-y-1.5 text-left">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <MessageSquare className="h-5 w-5 text-orange-500" /> Send feedback
            </DialogTitle>
            <DialogDescription>
              What's not working, or what could be better? A quick note is plenty.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <Textarea
              autoFocus
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") handleSubmit();
              }}
              placeholder="Describe the issue you're experiencing…"
              rows={5}
              maxLength={2000}
              className="resize-none"
              data-testid="input-feedback-message"
            />
          </div>

          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:gap-2">
            <Button
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              className="w-full sm:w-auto"
              data-testid="button-feedback-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="button-feedback-submit"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
